// Dependencies

import React from 'react';
import { Impression } from '@nerdwallet/analytics/react/impression';

// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { get, find } from 'lodash';

// Utils
import numeral from 'numeral';

// Components
import ConfigBuilder from '@nerdwallet/react-config-builder';
import { ClDataConfigProps, CompareFieldProps } from '../../shared/types';
import {
  ENTITY_NAME,
  FORM_NAME,
  SECTION_NAME,
} from '../../../../lib/analytics-constants';

import ScrollingTable from '../../../scrolling-table/scrolling-table';

// CSS
require('./compare-table.less');

type CompareTableProps = {
  config: any;
  compareFieldConfig: CompareFieldProps;
  data: ClDataConfigProps;
  currentCityId: string;
  onChange: (value: any, key: any) => void;
  customComponents: any;
};

const CompareTable = ({
  config,
  compareFieldConfig,
  data,
  currentCityId,
  onChange,
  customComponents,
}: CompareTableProps): JSX.Element => {
  const getRows = () => {
    const { cities, inputs } = data;
    const field = inputs.compareField;
    const sortedCities = cities.sort((a, b) => get(b, field) - get(a, field));
    let importantRow = -1;

    const rows = sortedCities.map((city, index) => {
      if (city.id === currentCityId) {
        importantRow = index;
      }

      const ranking = index + 1;
      const cityState = `${city.name}, ${city.state_abbr}`;
      const value = get(city, field);

      if (value) {
        return [
          ranking,
          cityState,
          numeral(value).format(compareFieldConfig.formatter),
        ];
      }

      return [ranking, cityState, '--'];
    });

    return {
      rows,
      importantRow,
    };
  };

  const renderHeader = () => {
    const { fieldDetails, inputs } = data;
    const fieldName = compareFieldConfig.label;
    const detail = find(fieldDetails, { field: inputs.compareField });
    const nationalAverage = get(detail, 'national_average');
    const formattedNationalAverage = numeral(nationalAverage).format(
      compareFieldConfig.formatter
    );
    const description = get(detail, 'description', '');

    return (
      <div className="cl-compare-results-details">
        {nationalAverage ? (
          <div>
            <b>National Average: </b>
            {formattedNationalAverage}
          </div>
        ) : null}
        {description ? (
          <div>
            <b>{`${fieldName}: `}</b>
            {description}
          </div>
        ) : null}
      </div>
    );
  };

  const { rows, importantRow } = getRows();

  const tableProps = {
    data: {
      head: ['Ranking', 'City', compareFieldConfig.columnName],
      rows,
      importantRow,
    },
  };

  const configBuilderProps = {
    customComponents,
    data,
    onChange,
  };

  return (
    <div className="cl-compare-results">
      <div className="cl-results-title cl-compare-results-title">
        <h2 className="cl-results-title-text">How Does it Compare?</h2>
      </div>

      <Impression
        eventType="form_impression"
        entityName={ENTITY_NAME.CITY_LIFE_COMPARE_FIELD}
        payload={{
          form_name: FORM_NAME.CITY_LIFE_COMPARE_FIELD,
          section_name: SECTION_NAME.CITY_LIFE_COMPARE_CITY,
          section_position: 5,
          page_number: null,
          total_pages: null,
        }}
      >
        {config.map((inputConfig, index) => (
          <ConfigBuilder
            {...configBuilderProps}
            viewConfig={inputConfig}
            key={`results-header-input-${index}`}
          />
        ))}
      </Impression>
      {renderHeader()}
      <ScrollingTable
        {...tableProps}
        className="cl-compare-results-scrolling-table"
      />
    </div>
  );
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
CompareTable.displayName = 'CompareTable';

export default CompareTable;
